import React, { useState, useEffect, useCallback } from 'react';
import { laundryApi, inventoryApi } from '../../services/api';
import { getTodayStr, getNextDayStr } from '../../utils/billing';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

function daysSince(dateStr) {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

// Falls back to sent + 2 days for batches sent before expected_return_date existed.
function getExpectedReturnDate(batch) {
  if (batch.expected_return_date) return batch.expected_return_date;
  const d = new Date(batch.sent_at);
  d.setDate(d.getDate() + 2);
  return d.toISOString().slice(0, 10);
}

function formatDateReadable(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

// Internal linen tracking — bedsheets/towels/pillow covers cycling between
// "clean in stock" (tracked as normal inventory) and "out at the laundry."
// Open to every role, same as Inventory itself; every send/receive is
// attributed to whoever did it (see routes/laundry.js audit_log entries).
export default function LaundryPage({ hotel, onSuccess }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [tab, setTab] = useState('out'); // 'out' | 'history'
  const [outBatches, setOutBatches] = useState([]);
  const [historyBatches, setHistoryBatches] = useState([]);
  const [linenItems, setLinenItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendRows, setSendRows] = useState([{ itemId: '', quantity: '' }]);
  const [sendNote, setSendNote] = useState('');
  const [vendorPersonName, setVendorPersonName] = useState('');
  const [vendorPersonPhone, setVendorPersonPhone] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');

  const [receivingBatch, setReceivingBatch] = useState(null);
  const [receiveQty, setReceiveQty] = useState({});
  const [receiveDate, setReceiveDate] = useState('');

  const load = useCallback(async () => {
    if (!hotel?.id) return;
    setLoading(true);
    try {
      const [out, history, items] = await Promise.all([
        laundryApi.listBatches(hotel.id, 'out'),
        laundryApi.listBatches(hotel.id, 'returned'),
        inventoryApi.items(hotel.id, null),
      ]);
      setOutBatches(out || []);
      setHistoryBatches(history || []);
      setLinenItems((items || []).filter(i => (i.item_type || i.itemType || 'guest') === 'linen'));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [hotel?.id]);

  useEffect(() => { load(); }, [load]);

  const openSendModal = () => {
    setSendRows([{ itemId: '', quantity: '' }]);
    setSendNote('');
    setVendorPersonName('');
    setVendorPersonPhone('');
    // Standard 2-day vendor turnaround, editable for a rush order or a
    // known-slower vendor.
    setExpectedReturnDate(getNextDayStr(getNextDayStr(getTodayStr())));
    setShowSendModal(true);
  };

  const addSendRow = () => setSendRows(prev => [...prev, { itemId: '', quantity: '' }]);
  const removeSendRow = (idx) => setSendRows(prev => prev.filter((_, i) => i !== idx));
  const updateSendRow = (idx, patch) => setSendRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const handleSend = async () => {
    const validRows = sendRows.filter(r => r.itemId && Number(r.quantity) > 0);
    if (validRows.length === 0) return await notify('Add at least one item with a quantity.');

    // Same "account for stock not yet reflected in a static number" guard used
    // when staging inventory-linked room services — check across all rows in
    // this same batch, not just against the raw DB quantity.
    const qtyByItem = {};
    for (const r of validRows) qtyByItem[r.itemId] = (qtyByItem[r.itemId] || 0) + Number(r.quantity);
    for (const [itemId, qty] of Object.entries(qtyByItem)) {
      const item = linenItems.find(i => i.id === itemId);
      if (item && qty > Number(item.quantity)) {
        const proceed = await confirm({ icon: '📦', title: 'Low Stock', body: `Only ${item.quantity} ${item.unit || 'pcs'} of "${item.name}" in stock. Send ${qty} anyway? Stock will go negative until reconciled.`, confirmText: 'Send Anyway' });
        if (!proceed) return;
      }
    }

    setProcessing(true);
    try {
      await laundryApi.createBatch({
        hotelId: hotel.id,
        note: sendNote || null,
        vendorPersonName: vendorPersonName || null,
        vendorPersonPhone: vendorPersonPhone || null,
        expectedReturnDate: expectedReturnDate || null,
        items: validRows.map(r => {
          const item = linenItems.find(i => i.id === r.itemId);
          return { itemId: r.itemId, itemName: item?.name, quantitySent: Number(r.quantity) };
        }),
      });
      onSuccess?.('Laundry batch sent.', 'success');
      setShowSendModal(false);
      load();
    } catch (err) { await notify(err.message); }
    setProcessing(false);
  };

  const outstandingOf = (item) => Math.max(0, item.quantitySent - (Number(item.quantityReturned) || 0));

  const openReceiveModal = (batch) => {
    setReceivingBatch(batch);
    const defaults = {};
    // Default to what's still outstanding, not the original amount sent — a
    // vendor returning a batch in two deliveries shouldn't have staff re-typing
    // the full original count on the second, smaller delivery.
    batch.items.forEach(i => { defaults[i.itemId] = String(outstandingOf(i)); });
    setReceiveQty(defaults);
    setReceiveDate(getTodayStr());
  };

  const handleReceive = async () => {
    setProcessing(true);
    try {
      // What's entered here is "how many came back just now" — the server adds
      // it onto whatever was already reconciled for this batch, it does not
      // overwrite the total.
      await laundryApi.receiveBatch(receivingBatch.id, {
        items: receivingBatch.items.map(i => ({ itemId: i.itemId, quantityReturned: Number(receiveQty[i.itemId]) || 0 })),
        receivedDate: receiveDate || undefined,
      });
      onSuccess?.('Laundry batch updated.', 'success');
      setReceivingBatch(null);
      load();
    } catch (err) { await notify(err.message); }
    setProcessing(false);
  };

  const handleCloseBatch = async (batch) => {
    const outstanding = batch.items.reduce((sum, i) => sum + outstandingOf(i), 0);
    const proceed = await confirm({
      icon: outstanding > 0 ? '⚠️' : '🧺',
      title: 'Close Batch',
      body: outstanding > 0
        ? `${outstanding} item(s) are still outstanding on this batch. Closing it now records them as a permanent loss — use this only once the vendor has confirmed nothing more is coming. Continue?`
        : 'Close this batch out?',
      confirmText: 'Close Batch',
      danger: outstanding > 0,
    });
    if (!proceed) return;
    setProcessing(true);
    try {
      await laundryApi.closeBatch(batch.id);
      onSuccess?.('Laundry batch closed.', 'success');
      load();
    } catch (err) { await notify(err.message); }
    setProcessing(false);
  };

  return (
    <div className="panel animate-in">
      <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span className="panel-title">🧺 Laundry</span>
        <button className="btn btn-sm" style={{ background: 'var(--accent)', color: 'white' }} onClick={openSendModal}>
          + Send to Laundry
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, padding: '12px 24px 0' }}>
        <button className={`btn btn-sm ${tab === 'out' ? '' : 'btn-ghost'}`} style={tab === 'out' ? { background: 'var(--accent)', color: 'white' } : {}} onClick={() => setTab('out')}>
          Currently Out {outBatches.length > 0 ? `(${outBatches.length})` : ''}
        </button>
        <button className={`btn btn-sm ${tab === 'history' ? '' : 'btn-ghost'}`} style={tab === 'history' ? { background: 'var(--accent)', color: 'white' } : {}} onClick={() => setTab('history')}>
          History
        </button>
      </div>

      <div style={{ padding: 24 }}>
        {linenItems.length === 0 && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(184,134,11,0.08)', color: '#b8860b', borderRadius: 8, fontSize: 12 }}>
            No inventory items are tagged as "Linen" yet. Go to Inventory &amp; Stock and set bedsheets/towels/pillow covers to the Linen usage type (or click their usage-type badge to cycle to it) before sending a batch.
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: 'var(--muted)' }}>
            <div className="spin" style={{ marginBottom: 16 }}>🌀</div>
            <div>Loading laundry records...</div>
          </div>
        ) : tab === 'out' ? (
          outBatches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '100px 0', background: 'var(--s1)', borderRadius: 12, color: 'var(--muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🧺</div>
              <div style={{ fontWeight: 700 }}>Nothing currently out at the laundry.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {outBatches.map(b => {
                const days = daysSince(b.sent_at);
                const dueDate = getExpectedReturnDate(b);
                const daysUntilDue = Math.ceil((new Date(dueDate) - new Date(new Date().toDateString())) / 86400000);
                const overdue = daysUntilDue <= 0;
                const partiallyBack = b.items.some(i => Number(i.quantityReturned) > 0);
                return (
                  <div key={b.id} style={{ background: 'var(--bg)', border: `1px solid ${overdue ? 'rgba(184,134,11,0.4)' : 'var(--border)'}`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {b.items.map(i => partiallyBack
                            ? `${i.itemName} — ${outstandingOf(i)} still out (of ${i.quantitySent})`
                            : `${i.itemName} x${i.quantitySent}`
                          ).join(', ')}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                          Sent by {b.sent_by_name || 'staff'} — {new Date(b.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {b.note ? ` · ${b.note}` : ''}
                          {partiallyBack ? ` · partially received so far` : ''}
                        </div>
                        {(b.vendor_person_name || b.vendor_person_phone) && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            🧺 Collected by {b.vendor_person_name || 'unnamed'}{b.vendor_person_phone ? ` · ${b.vendor_person_phone}` : ''}
                          </div>
                        )}
                        <div style={{ fontSize: 11, fontWeight: 700, color: overdue ? '#b8860b' : 'var(--teal)', marginTop: 2 }}>
                          {overdue
                            ? (daysUntilDue === 0 ? '⚠️ Due back today' : `⚠️ Overdue — was due ${formatDateReadable(dueDate)}`)
                            : `Expected back ${formatDateReadable(dueDate)}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: overdue ? '#b8860b' : 'var(--muted)' }}>
                          {days} day{days === 1 ? '' : 's'} out
                        </span>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleCloseBatch(b)} title="Accept remaining as lost and close this batch">
                          Close
                        </button>
                        <button className="btn btn-sm" style={{ background: 'var(--success)', color: 'white' }} onClick={() => openReceiveModal(b)}>
                          Receive
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          historyBatches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '100px 0', background: 'var(--s1)', borderRadius: 12, color: 'var(--muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
              <div style={{ fontWeight: 700 }}>No completed laundry batches yet.</div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr><th>Items</th><th>Sent</th><th>Received</th><th>Shortfall</th></tr>
                </thead>
                <tbody>
                  {historyBatches.map(b => {
                    const shortfall = b.items.reduce((sum, i) => sum + Math.max(0, i.quantitySent - (i.quantityReturned ?? 0)), 0);
                    return (
                      <tr key={b.id}>
                        <td>{b.items.map(i => `${i.itemName} (${i.quantityReturned ?? 0}/${i.quantitySent})`).join(', ')}</td>
                        <td>{b.sent_by_name || '—'}<br /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(b.sent_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span></td>
                        <td>{b.received_by_name || '—'}<br /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{b.received_at ? new Date(b.received_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}</span></td>
                        <td>{shortfall > 0 ? <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{shortfall} missing</span> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showSendModal && (
        <div className="overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-title">Send to Laundry</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {sendRows.map((row, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="input"
                    style={{ flex: '1 1 60%' }}
                    value={row.itemId}
                    onChange={e => updateSendRow(idx, { itemId: e.target.value })}
                  >
                    <option value="">-- Select linen item --</option>
                    {linenItems.map(i => (
                      <option key={i.id} value={i.id}>{i.name} ({i.quantity} {i.unit || 'pcs'} in stock)</option>
                    ))}
                  </select>
                  <input
                    type="number" min="1" placeholder="Qty"
                    className="input" style={{ width: 90 }}
                    value={row.quantity}
                    onChange={e => updateSendRow(idx, { quantity: e.target.value })}
                  />
                  {sendRows.length > 1 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSendRow(idx)}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addSendRow}>+ Add another item</button>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--muted)' }}>Collected By (optional)</label>
                  <input className="input" style={{ width: '100%' }} value={vendorPersonName} onChange={e => setVendorPersonName(e.target.value)} placeholder="Laundry person's name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--muted)' }}>Their Phone (optional)</label>
                  <input className="input" style={{ width: '100%' }} value={vendorPersonPhone} onChange={e => setVendorPersonPhone(e.target.value)} placeholder="10-digit number" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--muted)' }}>Expected Back On</label>
                <input type="date" className="input" style={{ width: '100%' }} min={getTodayStr()} value={expectedReturnDate} onChange={e => setExpectedReturnDate(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--muted)' }}>Note (optional)</label>
                <input className="input" style={{ width: '100%' }} value={sendNote} onChange={e => setSendNote(e.target.value)} placeholder="e.g. Vendor: Sri Laundry Services" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowSendModal(false)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--accent)', color: 'white' }} disabled={processing} onClick={handleSend}>
                {processing ? 'Sending…' : 'Send Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receivingBatch && (
        <div className="overlay" onClick={() => setReceivingBatch(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-title">Receive Laundry Batch</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginBottom: 12 }}>
              Enter how many came back in <b>this</b> delivery. If the vendor still owes some, leave those out — the batch stays under "Currently Out" for the rest and you can receive again later.
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Date received</label>
              <input
                type="date" className="input" style={{ width: '100%' }}
                value={receiveDate} max={getTodayStr()}
                onChange={e => setReceiveDate(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {receivingBatch.items.map(i => {
                const alreadyBack = Number(i.quantityReturned) || 0;
                const outstanding = outstandingOf(i);
                return (
                  <div key={i.itemId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {i.itemName}
                      <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                        {' '}(sent {i.quantitySent}{alreadyBack > 0 ? `, ${alreadyBack} already back` : ''}, {outstanding} outstanding)
                      </span>
                    </span>
                    <input
                      type="number" min="0" max={outstanding}
                      className="input" style={{ width: 90 }}
                      value={receiveQty[i.itemId] ?? ''}
                      onChange={e => {
                        const raw = e.target.value;
                        // The HTML `max` attribute alone doesn't stop someone from typing a
                        // larger number — it's only enforced on the up/down arrows — so a
                        // value over `outstanding` was silently accepted here and quietly
                        // capped server-side with no explanation. Clamp it live instead.
                        if (raw !== '' && Number(raw) > outstanding) {
                          setReceiveQty(prev => ({ ...prev, [i.itemId]: String(outstanding) }));
                          onSuccess?.(`Only ${outstanding} ${i.itemName} still outstanding — can't receive more than that.`, 'error');
                          return;
                        }
                        setReceiveQty(prev => ({ ...prev, [i.itemId]: raw }));
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setReceivingBatch(null)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--success)', color: 'white' }} disabled={processing} onClick={handleReceive}>
                {processing ? 'Confirming…' : 'Confirm Received'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
